import { useState } from "react"

import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/domains/common/components/tabs"
import { DesignSubItem } from "@/domains/navigation/subitems/design-subitem"
import { DesignPaletteSubItem } from "@/domains/navigation/subitems/design-palette-subitem"

const DESIGN_PALETTE_BREADCRUMB_ITEMS = [new DesignSubItem(), new DesignPaletteSubItem()]

const colorMap: Record<string, string> = {
  "p100-light": "bg-p100-light",
  "p100": "bg-p100",
  "p100-shade": "bg-p100-shade",
  "p200-light": "bg-p200-light",
  "p200": "bg-p200",
  "p200-shade": "bg-p200-shade",
  "p300-light": "bg-p300-light",
  "p300": "bg-p300",
  "p300-shade": "bg-p300-shade",
  "p400-light": "bg-p400-light",
  "p400": "bg-p400",
  "p400-shade": "bg-p400-shade",
  "p500": "bg-p500",
  "p500-light": "bg-p500",
  "p500-shade": "bg-p500",
}

const coreColors = ["p100", "p200", "p300", "p400", "p500"] as const
const lightColors = ["p100-light", "p200-light", "p300-light", "p400-light", "p500"] as const
const shadeColors = ["p100-shade", "p200-shade", "p300-shade", "p400-shade", "p500"] as const

const verticalColors = [
  "p100-light",
  "p100",
  "p100-shade",
  "p200-light",
  "p200",
  "p200-shade",
  "p300-light",
  "p300",
  "p300-shade",
  "p400-light",
  "p400",
  "p400-shade",
  "p500",
] as const

// Design System colors from the designer's palette
const designSystemColors: Record<string, string> = {
  "ds50": "#F5F7FF",
  "ds100": "#DBE4FF",
  "ds200": "#B3C2FF",
  "ds300": "#809BFF",
  "ds400": "#4D75FF",
  "dsPrimary": "#0000FF",
  "ds500": "#0000DC",
  "ds600": "#0000B8",
  "ds700": "#000093",
  "ds800": "#000070",
  "ds900": "#00004D",
}

// Design system colors (lightest to darkest, so darkest is at bottom when rendered)
const designSystemVerticalColors = [
  "ds50",
  "ds100",
  "ds200",
  "ds300",
  "ds400",
  "dsPrimary",
  "ds500",
  "ds600",
  "ds700",
  "ds800",
  "ds900",
] as const

// Levels colors (lightest to darkest, so darkest is at bottom when rendered)
const levelsVerticalColors = [
  "p100-light",
  "p100",
  "p100-shade",
  "p200-light",
  "p200",
  "p200-shade",
  "p300-light",
  "p300",
  "p300-shade",
  "p400-light",
  "p400",
  "p400-shade",
  "p500",
] as const

const ColorColumn = ({ colors, columnTitle }: { colors: readonly string[]; columnTitle?: string }) => {
  return (
    <div className="grid grid-cols-1" style={{ width: "320px" }}>
      {columnTitle && (
        <div className="mb-2 text-sm font-medium text-t200">{columnTitle}</div>
      )}
      {colors.map((colorName) => (
        <div
          key={colorName}
          className={colorMap[colorName]}
          style={{ height: "80px", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <span className="text-t100 text-sm">{colorName}</span>
        </div>
      ))}
    </div>
  )
}

const GridView = () => {
  return (
    <div className="flex">
      <ColorColumn colors={lightColors} />
      <ColorColumn colors={coreColors} />
      <ColorColumn colors={shadeColors} />
    </div>
  )
}

const VerticalColorColumn = ({
  title,
  colors,
  isDesignSystem = false,
  topGap = 0,
}: {
  title: string
  colors: readonly string[]
  isDesignSystem?: boolean
  topGap?: number
}) => {
  return (
    <div className="grid grid-cols-1" style={{ width: "320px" }}>
      <div
        style={{
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        className="text-t100 text-sm font-medium"
      >
        {title}
      </div>
      {topGap > 0 && <div style={{ height: `${topGap}px` }} />}
      {colors.map((colorName) => {
        // For design system, use hex colors; for p500 in design system, use the p500 class
        const isP500 = colorName === "p500"
        const colorStyle = isDesignSystem && !isP500 ? { backgroundColor: designSystemColors[colorName] } : {}
        const className = isDesignSystem && isP500 ? colorMap["p500"] : isDesignSystem ? "" : colorMap[colorName] ?? "bg-p500"

        return (
          <div
            key={colorName}
            className={className}
            style={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...colorStyle,
            }}
          >
            <span className="text-t100 text-sm">{colorName}</span>
          </div>
        )
      })}
    </div>
  )
}

const VerticalView = () => {
  // Add p500 at the bottom of design system colors
  const designSystemWithP500 = [...designSystemVerticalColors, "p500"] as const

  // Calculate gap needed at top of design system column
  const levelsCount = levelsVerticalColors.length
  const designSystemCount = designSystemWithP500.length
  const topGap = (levelsCount - designSystemCount) * 60

  return (
    <div className="flex" style={{ gap: "8px" }}>
      <VerticalColorColumn title="Levels" colors={levelsVerticalColors} isDesignSystem={false} />
      <VerticalColorColumn title="Design System" colors={designSystemWithP500} isDesignSystem={true} topGap={topGap} />
    </div>
  )
}

const DesignPalette = () => {
  const [activeTab, setActiveTab] = useState("grid")

  return (
    <PageContainer>
      <MetaData title="Design | Design Palette" />
      <PageTitle title="Design Palette" subTitle="Color palette reference" items={DESIGN_PALETTE_BREADCRUMB_ITEMS} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="vertical">Vertical View</TabsTrigger>
        </TabsList>
        <TabsContent value="grid">
          <GridView />
        </TabsContent>
        <TabsContent value="vertical">
          <VerticalView />
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}

export default DesignPalette

